
import { toast } from '@/hooks/use-toast';
import { supabase } from './client';

// Track login attempts for rate limiting
const loginAttempts = new Map<string, { count: number, timestamp: number }>();
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes in milliseconds

// Helper to check if a user is rate limited
const isRateLimited = (email: string): boolean => {
  const now = Date.now();
  const userAttempts = loginAttempts.get(email);
  
  if (!userAttempts) return false;
  
  // Clear lockout if time has expired
  if (userAttempts.count >= MAX_LOGIN_ATTEMPTS && now - userAttempts.timestamp > LOCKOUT_DURATION) {
    loginAttempts.delete(email);
    return false;
  }
  
  return userAttempts.count >= MAX_LOGIN_ATTEMPTS;
};

// Helper to record a failed login attempt
const recordLoginAttempt = (email: string): void => {
  const now = Date.now();
  const userAttempts = loginAttempts.get(email);
  
  if (!userAttempts) {
    loginAttempts.set(email, { count: 1, timestamp: now });
  } else {
    loginAttempts.set(email, { 
      count: userAttempts.count + 1, 
      timestamp: now 
    });
  }
};

// Reset login attempts on successful login
const resetLoginAttempts = (email: string): void => {
  loginAttempts.delete(email);
};

export const signIn = async (email: string, password: string) => {
  try {
    // Check if user is rate limited
    if (isRateLimited(email)) {
      return { 
        data: null, 
        error: new Error('Too many failed login attempts. Please try again later.') 
      };
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      recordLoginAttempt(email);
      throw error;
    }
    
    // Reset login attempts on successful login
    resetLoginAttempts(email);
    
    return { data, error: null };
  } catch (error) {
    console.error('Error signing in:', error);
    return { data: null, error };
  }
};

export const signUp = async (email: string, password: string, fullName?: string) => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName || '',
        },
      },
    });
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error signing up:', error);
    return { data: null, error };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    
    if (error) throw error;
    
    return { error: null };
  } catch (error) {
    console.error('Error signing out:', error);
    return { error };
  }
};

export const getCurrentUser = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const resetPassword = async (email: string) => {
  try {
    // Get the current site URL dynamically
    const siteUrl = window.location.origin;
    console.log("Reset password using site URL:", siteUrl);
    
    // Try to use the current site URL for password reset
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${siteUrl}/reset-password`,
    });
    
    if (error) throw error;
    
    return { error: null };
  } catch (error) {
    console.error('Error resetting password:', error);
    return { error };
  }
};

// Function to verify email OTP code (for future multi-factor auth)
export const verifyOtp = async (email: string, token: string) => {
  try {
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email'
    });
    
    if (error) throw error;
    
    return { data, error: null };
  } catch (error) {
    console.error('Error verifying OTP:', error);
    return { data: null, error };
  }
};
